import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { VeracordLogo } from "@/components/ui/VeracordLogo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, FileText, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import Lottie from "lottie-react";
import successAnimation from "@/assets/animations/success.json";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type TokenStatus = "loading" | "valid" | "used" | "expired" | "not_found";

interface ValidTokenData {
  policyName: string;
  versionNumber: number;
  category: string;
  fileUrl: string;
  pdfUrl: string | null;
}

export default function AcknowledgeMagicLink() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState<TokenStatus>("loading");
  const [tokenData, setTokenData] = useState<ValidTokenData | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);

  useEffect(() => {
    if (!token) {
      setStatus("not_found");
      return;
    }
    (async () => {
      const { data, error } = await supabase.functions.invoke("validate-acknowledgement-token", {
        body: { token },
      });
      if (error || !data) {
        setStatus("not_found");
        return;
      }
      setStatus(data.status);
      if (data.status === "valid") {
        setTokenData({
          policyName: data.policyName,
          versionNumber: data.versionNumber,
          category: data.category,
          fileUrl: data.fileUrl,
          pdfUrl: data.pdfUrl,
        });
      }
    })();
  }, [token]);

  const handleViewDocument = useCallback(async () => {
    if (!tokenData) return;
    setPdfOpen(true);
    if (pdfBlobUrl) return; // already loaded

    setPdfLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/magic-link-document-download?token=${encodeURIComponent(token)}`
      );
      if (!res.ok) throw new Error("Failed to fetch document");
      const blob = await res.blob();
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("PDF fetch error:", err);
    } finally {
      setPdfLoading(false);
    }
  }, [tokenData, token, pdfBlobUrl]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const { data, error } = await supabase.functions.invoke("redeem-acknowledgement-token", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) {
        setAcknowledged(true);
      }
    } catch (err) {
      console.error("Acknowledge error:", err);
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <VeracordLogo size="md" color="hsl(var(--foreground))" />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Validating link…</p>
          </div>
        )}

        {status === "valid" && !acknowledged && tokenData && (
          <div className="w-full max-w-md space-y-6">
            <div className="space-y-2 text-center">
              <h1 className="text-xl font-semibold text-foreground">Policy Acknowledgement</h1>
              <p className="text-sm text-muted-foreground">
                Please review and acknowledge the following policy.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-card p-6 space-y-4">
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">{tokenData.policyName}</h2>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    v{tokenData.versionNumber}
                  </Badge>
                  {tokenData.category && (
                    <Badge variant="outline" className="text-xs">
                      {tokenData.category}
                    </Badge>
                  )}
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleViewDocument}
              >
                <FileText className="h-4 w-4" />
                View Document
              </Button>

              <Button
                className="w-full gap-2"
                style={{ backgroundColor: "#339999" }}
                onClick={handleAcknowledge}
                disabled={acknowledging}
              >
                {acknowledging ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {acknowledging ? "Processing…" : "Acknowledge"}
              </Button>
            </div>
          </div>
        )}

        {status === "valid" && acknowledged && (
          <div className="w-full max-w-sm text-center space-y-4 flex flex-col items-center justify-center">
            <div className="w-32 h-32">
              <Lottie animationData={successAnimation} loop={false} />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Acknowledgement Recorded.</h2>
            <p className="text-sm text-muted-foreground">Your acknowledgement has been saved.</p>
            <p className="text-xs text-muted-foreground/70">You can close this tab.</p>
          </div>
        )}

        {status === "used" && (
          <div className="w-full max-w-sm text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">This link has already been used.</h2>
            <p className="text-sm text-muted-foreground">Your acknowledgement was previously recorded.</p>
            <Button variant="outline" asChild>
              <a href="/auth">Log in to Veracord</a>
            </Button>
          </div>
        )}

        {(status === "expired" || status === "not_found") && (
          <div className="w-full max-w-sm text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              {status === "expired" ? (
                <Clock className="h-8 w-8 text-muted-foreground" />
              ) : (
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-foreground">This link has expired.</h2>
            <p className="text-sm text-muted-foreground">
              Please log in to Veracord to acknowledge this policy.
            </p>
            <Button variant="outline" asChild>
              <a href="/auth">Log in to Veracord</a>
            </Button>
          </div>
        )}
      </main>

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {pdfLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {pdfBlobUrl && (
            <Document
              file={pdfBlobUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={680}
                  className="mb-4"
                />
              ))}
            </Document>
          )}
          {!pdfLoading && !pdfBlobUrl && (
            <p className="text-center text-sm text-muted-foreground py-8">
              Unable to load document.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
